/**
 * The only place in apps/web that calls `fetch`.
 *
 * Hooks call this; components and pages call hooks (§5). ESLint blocks importing
 * this module from `components/` and `pages/`.
 */
import type { ApiError } from '@henlytics/types'
import { config } from '../config/env'
import { getSession } from '../store/session'

export class ApiRequestError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message)
    this.name = 'ApiRequestError'
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE'
  body?: unknown
  signal?: AbortSignal
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const session = getSession()

  const headers: Record<string, string> = { accept: 'application/json' }
  if (options.body !== undefined) headers['content-type'] = 'application/json'
  if (session.accessToken) headers.authorization = `Bearer ${session.accessToken}`
  if (session.farmId) headers['x-farm-id'] = session.farmId

  const response = await fetch(`${config.apiUrl}${path}`, {
    method: options.method ?? 'GET',
    headers,
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
    signal: options.signal,
  })

  const payload: unknown = response.status === 204 ? null : await response.json()

  if (!response.ok) {
    const error = (payload as ApiError | null)?.error
    throw new ApiRequestError(
      response.status,
      error?.code ?? 'unknown_error',
      error?.message ?? response.statusText,
      error?.details,
    )
  }

  return (payload as { data: T }).data
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) => request<T>(path, { signal }),
  post: <T>(path: string, body: unknown) => request<T>(path, { method: 'POST', body }),
  patch: <T>(path: string, body: unknown) => request<T>(path, { method: 'PATCH', body }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
}
